/**
 * usePresetSharing — handle the "share preset" flow.
 *
 * Self-contained: owns shareLoading + copiedShareId state, calls the share
 * API, copies the resulting URL to the clipboard, and surfaces toasts.
 * Audio state is intentionally not touched.
 */
import { useCallback, useState } from 'react';
import { createShareLink } from '../services/presetApi';
import { buildShareUrl } from '../lib/presets';

export function usePresetSharing({ toast } = {}) {
  const [shareLoading, setShareLoading] = useState(false);
  const [copiedShareId, setCopiedShareId] = useState(null);

  const sharePreset = useCallback(async (preset) => {
    if (!preset) return;
    setShareLoading(true);
    try {
      const { share_id } = await createShareLink(preset.id);
      const url = buildShareUrl(share_id);
      await navigator.clipboard.writeText(url);
      setCopiedShareId(preset.id);
      toast?.({
        title: 'Link Copied!',
        description: `Share link for "${preset.name}" has been copied to clipboard.`,
      });
      setTimeout(() => setCopiedShareId(null), 3000);
    } catch (e) {
      console.error('Error sharing preset:', e);
      toast?.({
        title: 'Share Failed',
        description: 'Could not create a share link. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setShareLoading(false);
    }
  }, [toast]);

  return { shareLoading, copiedShareId, sharePreset };
}

export default usePresetSharing;
